import {
  FanguardApiContractsClientEmailTemplateCreateRequestModel,
  FanguardApiContractsClientEmailTemplateModel,
  FanguardApiContractsClientSendTestEmailRequestModel,
} from 'src/api/generated';

import { patch, post, remove } from '@/api/api';
import { emailTemplatesQueries, EMAIL_TEMPLATES_URI } from '@/api/queryKeys';
import { useMutation, useQuery, UseQueryResult } from '@tanstack/react-query';
import { App } from 'antd';
import { useTranslation } from 'react-i18next';
import { useMutationWithMessages } from './helpers';
import { AxiosResponse } from 'axios';

interface SendTestEmailPayload extends FanguardApiContractsClientSendTestEmailRequestModel {
  id: string;
}

const useGetEmailTemplates = (): UseQueryResult<FanguardApiContractsClientEmailTemplateModel[], Error> => {
  return useQuery({
    ...emailTemplatesQueries.list,
  });
};

const useGetEmailTemplate = (id: string): UseQueryResult<FanguardApiContractsClientEmailTemplateModel, Error> => {
  return useQuery({
    ...emailTemplatesQueries.detail(id),
    enabled: !!id,
  });
};

const useSendTestEmail = () => {
  const { message } = App.useApp();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: async (data: SendTestEmailPayload) => {
      const response = await post({ uri: `${EMAIL_TEMPLATES_URI}/${data.id}/send-test`, data });
      return response.data;
    },
    onSuccess: () => {
      message.success(t('emailTemplates.newEditEmailTemplate.sentTestEmailSuccess'));
    },
    onError: () => {
      message.error(t('emailTemplates.newEditEmailTemplate.sentTestEmailError'));
    },
  });
};

const useCreateEmailTemplate = () => {
  return useMutationWithMessages<
    AxiosResponse<FanguardApiContractsClientEmailTemplateModel>,
    Error,
    FanguardApiContractsClientEmailTemplateCreateRequestModel
  >((data) => post({ uri: EMAIL_TEMPLATES_URI, data }), {
    action: 'create',
    entityType: 'emailTemplate',
  });
};

const useUpdateEmailTemplate = (id: string) => {
  return useMutationWithMessages<
    AxiosResponse<FanguardApiContractsClientEmailTemplateModel>,
    Error,
    FanguardApiContractsClientEmailTemplateModel
  >((data) => patch({ uri: `${EMAIL_TEMPLATES_URI}/${data.id}`, data }), {
    action: 'update',
    entityType: 'emailTemplate',
    queryKeysToInvalidate: [emailTemplatesQueries.detail(id).queryKey],
  });
};

const useDeleteEmailTemplate = () => {
  return useMutationWithMessages((id: string) => remove({ uri: `${EMAIL_TEMPLATES_URI}/${id}` }), {
    action: 'delete',
    entityType: 'emailTemplate',
    queryKeysToInvalidate: [emailTemplatesQueries.list._def],
  });
};

export {
  useGetEmailTemplates,
  useSendTestEmail,
  useGetEmailTemplate,
  useCreateEmailTemplate,
  useUpdateEmailTemplate,
  useDeleteEmailTemplate,
};
